import type {
  ModelOption,
  OrganizationModelPolicy,
  OrchestratorEffortSelection,
  OrchestratorModelSelection
} from '@axilon-apps/domains-ontology-ui-state'
import { catalogEffortLevels, effortLevelLabel } from '@axilon-apps/domains-ontology-ui-state'

import { allowedModelOptions, isModelAllowed, modelLabel } from '../../lib/model-policy-labels'

export { modelLabel }

/** Label for letting the org policy pick the model (no conversation override). */
export const AUTO_MODEL_LABEL = 'Auto'

/** Short model name for the composer trigger (no provider suffix). */
export function composerTriggerModelLabel(
  policy: OrganizationModelPolicy,
  value: OrchestratorModelSelection | null
): string {
  if (value !== null) return modelLabel(policy, value.provider, value.model)
  return AUTO_MODEL_LABEL
}

/**
 * Split the org catalog into the selector's first screen and its "Other
 * models" submenu. A catalog with no primary model at all (for example, a
 * Bedrock-only deployment) keeps every model on the first screen rather
 * than hiding all of them behind a submenu.
 */
export function partitionModelOptions(policy: OrganizationModelPolicy): {
  primary: ModelOption[]
  other: ModelOption[]
} {
  const primary: ModelOption[] = []
  const other: ModelOption[] = []
  for (const option of allowedModelOptions(policy)) {
    if (option.tier === 'other') other.push(option)
    else primary.push(option)
  }
  if (primary.length === 0) return { primary: other, other: [] }
  return { primary, other }
}

/** Resolve Auto to its concrete model for model-specific preferences. */
export function resolveModelSelection(
  policy: OrganizationModelPolicy,
  selection: OrchestratorModelSelection | null
): OrchestratorModelSelection | null {
  if (selection !== null) {
    return isModelAllowed(policy, selection.provider, selection.model) ? selection : null
  }
  return policy.taskClassRouting.standard?.resolvedTarget ?? null
}

/**
 * Whether the composer should show the Thinking picker for this choice.
 * Auto never does — it omits effort so the task-class policy applies.
 * A pinned model follows the catalog effort list.
 */
export function selectionSupportsEffort(
  policy: OrganizationModelPolicy,
  selection: OrchestratorModelSelection | null
): boolean {
  return selectionEffortLevels(policy, selection).length > 0
}

export function selectionEffortLevels(
  policy: OrganizationModelPolicy,
  selection: OrchestratorModelSelection | null
): ReturnType<typeof catalogEffortLevels> {
  if (selection === null) return []
  if (!isModelAllowed(policy, selection.provider, selection.model)) return []
  return catalogEffortLevels(
    policy.catalog.find(
      (option) => option.provider === selection.provider && option.model === selection.model
    )
  )
}

export function clampEffortToCatalog(
  levels: ReturnType<typeof catalogEffortLevels>,
  effort: OrchestratorEffortSelection | null
): OrchestratorEffortSelection | null {
  if (levels.length === 0) return null
  if (effort !== null && levels.includes(effort)) return effort
  return levels.includes('medium') ? 'medium' : (levels[0] ?? null)
}

/** Compact trigger text: model, optionally · effort when overridden and supported. */
export function composerTriggerLabel(
  policy: OrganizationModelPolicy,
  model: OrchestratorModelSelection | null,
  effort: OrchestratorEffortSelection | null
): string {
  const modelPart = composerTriggerModelLabel(policy, model)
  if (effort === null || !selectionSupportsEffort(policy, model)) return modelPart
  return `${modelPart} · ${effortLevelLabel(effort)}`
}
